// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

import { ReactNode, startTransition, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut, Settings, Menu, X } from 'lucide-react';
import { useSession, hasAnyRole } from '../context/SessionContext';
import { portalNavItems } from '../config/navConfig';
import Logo from './Logo';
import OfflineBanner from './OfflineBanner';
import { useAppVersion } from '../hooks/useAppVersion';

type Props = {
  children: ReactNode;
};

export default function PortalLayout({ children }: Props) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const appVersion = useAppVersion();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { session, clearSession } = useSession();

  const handleLogout = () => {
    // react-router 7 applies navigate() inside a React transition. Clearing the session
    // as an urgent update would re-render the still-current route with no session, and the
    // enclosing ProtectedRoute's redirect (to /portal) would supersede this navigation. Put
    // the clear in the same transition so both commit together and /login renders.
    navigate('/login', { replace: true });
    startTransition(() => clearSession());
  };

  const isActive = (path: string) =>
    path === '/portal' ? pathname === '/portal' : pathname.startsWith(path);

  const isAdmin = hasAnyRole(session, ['admin']);

  const showAlphaBanner = import.meta.env.VITE_ALPHA_BANNER !== 'false';

  return (
    <div className="flex min-h-screen flex-col bg-slate-50 text-slate-900">
      {showAlphaBanner && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-center text-sm text-amber-800">
          <strong>Alpha</strong> — This is an early preview. Features may change or break. Your feedback helps us build something great.
        </div>
      )}
      <div className="flex flex-1">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:w-64 lg:flex-col lg:border-r lg:border-slate-200 lg:bg-white">
        <div className="flex h-16 items-center border-b border-slate-200 px-6">
          <Logo variant="icon" size="md" /><span className="text-lg font-semibold text-brand-700 ml-2">My Club</span>
        </div>
        <nav className="flex-1 space-y-1 px-3 py-4" aria-label="Portal navigation">
          {portalNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition ${
                isActive(item.path)
                  ? 'bg-brand-100 text-brand-800'
                  : 'text-slate-700 hover:bg-slate-100'
              }`}
              aria-current={isActive(item.path) ? 'page' : undefined}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
              {t(item.labelKey)}
            </Link>
          ))}
        </nav>
        <div className="border-t border-slate-200 p-3 space-y-1">
          {isAdmin && (
            <Link
              to="/dashboard"
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-brand-700 transition hover:bg-brand-50"
            >
              <Settings className="h-5 w-5" aria-hidden="true" />
              {t('nav.switchToAdmin')}
            </Link>
          )}
          <button
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
            aria-label={t('common.logOut')}
          >
            <LogOut className="h-5 w-5" aria-hidden="true" />
            {t('common.logOut')}
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="flex min-w-0 flex-1 flex-col lg:ml-0">
        <header className="sticky top-0 z-10 bg-white shadow-sm lg:hidden">
          <div className="flex items-center justify-between px-4 py-3">
            <Logo variant="icon" size="md" /><span className="text-lg font-semibold text-brand-700 ml-2">My Club</span>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="rounded-lg p-2 text-slate-600 hover:bg-slate-100"
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
          {mobileMenuOpen && (
            <nav className="border-t border-slate-200 bg-white px-4 py-2" aria-label="Mobile portal navigation">
              {portalNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition ${
                    isActive(item.path)
                      ? 'bg-brand-100 text-brand-800'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <item.icon className="h-5 w-5" aria-hidden="true" />
                  {t(item.labelKey)}
                </Link>
              ))}
              {isAdmin && (
                <Link
                  to="/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-brand-700 transition hover:bg-brand-50"
                >
                  <Settings className="h-5 w-5" aria-hidden="true" />
                  {t('nav.switchToAdmin')}
                </Link>
              )}
              <button
                onClick={() => { setMobileMenuOpen(false); handleLogout(); }}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
              >
                <LogOut className="h-5 w-5" aria-hidden="true" />
                {t('common.logOut')}
              </button>
            </nav>
          )}
        </header>

        {/* Offline indicator */}
        <OfflineBanner />

        <main className="min-w-0 flex-1 overflow-x-hidden px-4 py-6 lg:px-6">{children}</main>

        {/* Footer */}
        <footer className="hidden lg:block border-t border-slate-200 bg-white px-6 py-4">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{t('common.copyright', { year: new Date().getFullYear() })}{appVersion && ` · ${appVersion}`}</span>
            <div className="flex gap-4">
              <Link to="/terms" className="hover:text-brand-600">{t('common.terms')}</Link>
              <Link to="/privacy" className="hover:text-brand-600">{t('common.privacy')}</Link>
              <Link to="/cookies" className="hover:text-brand-600">{t('common.cookies')}</Link>
            </div>
          </div>
        </footer>

        {/* Mobile Bottom Navigation */}
        <nav className="fixed bottom-0 left-0 right-0 z-10 border-t border-slate-200 bg-white lg:hidden" aria-label="Bottom navigation">
          <div className="grid grid-cols-6 gap-0">
            {portalNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center min-h-[56px] px-1 py-2 transition-colors ${
                  isActive(item.path)
                    ? 'text-brand-700 bg-brand-50'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
                aria-current={isActive(item.path) ? 'page' : undefined}
                aria-label={t(item.labelKey)}
                title={t(item.labelKey)}
              >
                <item.icon className="h-6 w-6" aria-hidden="true" />
                <span className="mt-1 text-[10px] font-medium leading-none">{t(item.labelKey)}</span>
              </Link>
            ))}
          </div>
        </nav>
        <div className="h-16 lg:hidden" aria-hidden="true" />
      </div>
      </div>
    </div>
  );
}
